import { alpha, Box, Button, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TableRow } from '@mui/material';
import Grid from '@mui/material/Grid2';
import 'jspdf-autotable';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MainBorderRadius, TableHeader } from '../../../app/AppStyles';
import { DisplayName, GetDate } from '../../../app/AppUtils';
import AutocompleteSelector from '../../subcomponents/AutocompleteSelector';
import ContentLoader from '../../subcomponents/ContentLoader';
import TablePaginationActions from '../../subcomponents/TablePaginationActions';
import { fetchReportActivities, fetchReportPerformances, resetReportFilterValues } from '../reportFiltersSlice';
import ExportExcelWintersport from './ExportExcelWintersport';
import { fetchWintersportReport } from './reportWintersportSlice';

export default function ReportWintersportTab() {
    const dispatch = useDispatch();

    const [searching, setSearching] = useState(false);
    const [selectedActivity, setSelectedActivity] = useState();
    const [selectedPerformance, setSelectedPerformance] = useState();

    const { appLanguage, resourceFinal } = useSelector((state) => state.settings.resource.resources);
    const { wintersportReport, wintersportReportStatus } = useSelector((state) => state.reports.wintersport);
    const { reportActivities, reportActivitiesStatus, reportPerformances, reportPerformancesStatus } = useSelector((state) => state.reports.reportFilters);

    useEffect(() => {
        dispatch(resetReportFilterValues());
        dispatch(fetchReportActivities());
    }, [dispatch]);

    useEffect(() => {
        if (selectedActivity)
            dispatch(fetchReportPerformances({ activityId: selectedActivity.activityId, key: "subscriptions" }));
    }, [dispatch, selectedActivity]);

    useEffect(() => {
        if (wintersportReportStatus === "success" || wintersportReportStatus === "failed")
            setSearching(false);
    }, [wintersportReportStatus]);

    const handleSearchReportClick = () => {
        setSearching(true);
        dispatch(fetchWintersportReport(selectedPerformance.performanceId));
    };

    const TableFragment = () => {
        const [page, setPage] = useState(0);
        const [rowsPerPage, setRowsPerPage] = useState(wintersportReport?.length < 100 ? -1 : 100);

        const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - wintersportReport?.students?.length) : 0;

        const handleChangePage = (event, newPage) => {
            setPage(newPage);
        };

        const handleChangeRowsPerPage = (event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
        };

        return (
            <>
                <TableBody>
                    {(rowsPerPage > 0 ? wintersportReport?.students?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : wintersportReport?.students)?.map((item, index) =>
                        <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: 'white.main' }, height: 63 }}>
                            <TableCell align="left">{DisplayName(item.studentFirstName, item.studentInsertion, item.studentLastName)}</TableCell>
                            <TableCell align="center">{GetDate(item.studentDateOfBirth)}</TableCell>
                            <TableCell align="center">{item.studentSport}</TableCell>
                            <TableCell align="center">{item.studentHeight}</TableCell>
                            <TableCell align="center">{item.studentWeight}</TableCell>
                            <TableCell align="center">{item.studentShoeSize}</TableCell>
                            <TableCell align="center">{item.studentExperience}</TableCell>
                            <TableCell align="center">{item.studentMaterial}</TableCell>
                            <TableCell align="center">{item.studentGender}</TableCell>
                            <TableCell align="center">{item.studentMedication}</TableCell>
                            <TableCell align="center">{item.studentEmail}</TableCell>
                            <TableCell align="center">{item.studentPhone}</TableCell>
                            <TableCell align="center">{item.studentAllergy}</TableCell>
                            <TableCell align="center">{item.emergencyName}</TableCell>
                            <TableCell align="center">{item.emergencyPhone}</TableCell>
                        </TableRow>
                    )}
                    {(rowsPerPage > 0 ? wintersportReport?.students?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : wintersportReport?.students)?.length === 0 && <TableRow><TableCell colSpan={13}>{resourceFinal[appLanguage].messages.FILTER_RESULT_EMPTY}</TableCell></TableRow>}
                    {emptyRows > 0 && <TableRow style={{ height: 63 * emptyRows }}><TableCell colSpan={13} /></TableRow>}
                </TableBody>
                <TableFooter>
                    <TableRow>
                        <TablePagination
                            sx={{ border: 0 }}
                            rowsPerPageOptions={[10, 25, 50,
                                {
                                    label: wintersportReport?.length < 100 ? resourceFinal[appLanguage].words.ALL : '100',
                                    value: wintersportReport?.length < 100 ? -1 : 100
                                }
                            ]}
                            count={wintersportReport?.students ? wintersportReport?.students?.length : 0}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                            labelDisplayedRows={({ from, to, count }) => { return `${from}-${to} ${resourceFinal[appLanguage].messages.OF_THE} ${count}`; }}
                            labelRowsPerPage={resourceFinal[appLanguage].messages.TABLE_ROWS_PER_PAGE}
                        />
                    </TableRow>
                </TableFooter>
            </>
        );
    };

    return (
        <>
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto' }}>
                <Grid container direction="row" spacing={1} sx={{ bgcolor: theme => alpha(theme.palette.white.main, 0.3), borderRadius: MainBorderRadius, p: 1 }}>
                    <Grid size={3}>
                        <AutocompleteSelector
                            label={resourceFinal[appLanguage].words.ACTIVITY}
                            options={reportActivities ?? []}
                            value={selectedActivity ?? null}
                            onChange={(val) => {
                                setSelectedPerformance();
                                setSelectedActivity(val)
                            }}
                            getOptionLabel={(option) => `${option.activityName} (${option.performancesUnsubscriptionCount})`}
                            getOptionKey={(option) => option.activityId}
                            isOptionEqualToValue={(option, value) => option.activityId === value?.activityId}
                            disabled={reportActivitiesStatus !== "success"}
                        />
                    </Grid>
                    <Grid size={3}>
                        <AutocompleteSelector
                            label={resourceFinal[appLanguage].words.PERFORMANCE}
                            options={reportPerformances ?? []}
                            value={selectedPerformance ?? null}
                            onChange={(val) => {
                                setSelectedPerformance(val)
                            }}
                            getOptionLabel={(option) => option.performanceName}
                            getOptionKey={(option) => option.performanceId}
                            isOptionEqualToValue={(option, value) => option.performanceId === value?.performanceId}
                            disabled={reportPerformancesStatus !== "success"}
                        />
                    </Grid>
                    <Grid size={6}>
                        <Button
                            variant="outlined"
                            color="secondary"
                            disabled={!selectedPerformance}
                            sx={{ mr: 1, height: 1 }}
                            onClick={handleSearchReportClick}
                        >
                            {resourceFinal[appLanguage].words.REPORT_GENERATE}
                        </Button>
                        <ExportExcelWintersport disabled={searching || !wintersportReport} jsonData={wintersportReport} />
                    </Grid>
                </Grid>
                <ContentLoader
                    successCondition={searching === false}
                    errorCondition={wintersportReportStatus === "failed"}
                    content={
                        <TableContainer>
                            <Table size="small" id="#report">
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="left" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.STUDENT}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.USER_DATA_BIRTHDATE}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.USER_DATA_SPORT}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.USER_DATA_HEIGHT}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.USER_DATA_WEIGHT}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.USER_DATA_SHOE_SIZE}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.USER_DATA_SKIEXPERIENCE}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.USER_DATA_OWNED_MATERIAL}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.USER_DATA_GENDER}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.USER_DATA_MEDICATION}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.USER_DATA_EMAIL_PRIVATE}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.USER_DATA_PHONENUMBER}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.USER_DATA_ALLERGY}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.USER_DATA_EMERGENCY_CONTACT_NAME}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.USER_DATA_EMERGENCY_CONTACT_NUMBER}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableFragment />
                            </Table>
                        </TableContainer>
                    }
                />
            </Box>
        </>
    );
}
